import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity - The quantity captured when Add was clicked.
 * @property {number} generation - The variant-change generation active when Add was clicked.
 * @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
 * @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
 * @property {string | null} variantResolutionUrl - A section-rendering URL that resolves the clicked selection.
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Adds queued while a variant change is in flight. Each entry captures the selection state and
   * generation active when Add was clicked, then resolves that selection at drain time.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * The in-flight variant-change section fetch promise. The queue drain awaits this before
   * reading the resolved variant id.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    target?.addEventListener(
      'change',
      (e) => {
        if (/** @type {HTMLElement} */ (e.target)?.closest('variant-picker')) {
          this.#clearNativeErrors();
        }
      },
      { signal }
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('cart:updated', this.#onCustomCartUpdate, { signal });

    // Initial cart evaluation for real-time luxury scarcity using cache
    if (!window.thkCartCache) {
      window.thkCartCache = [];
    }
    this.#updateCartQuantity();

    // Background sync cart in case cache was cold or updated elsewhere
    this.#refreshCart()
      .then((cart) => {
        if (cart?.items) window.thkCartCache = cart.items;
        this.#updateCartQuantity(cart);
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.warn('[product-form] Error fetching /cart.js:', e);
    }

    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
      return cartItemsComponent.fetchCartData();
    }
    return null;
  }

  /**
   * Helper to retrieve clean default button text (never "Max Quantity in Cart" or "Sold Out")
   * @param {HTMLButtonElement | null} button
   * @returns {string}
   */
  #getDefaultButtonText(button) {
    const rawDefault = button?.dataset?.defaultText?.trim();
    if (rawDefault && !['Max Quantity in Cart', 'Sold Out', 'Adding...'].includes(rawDefault)) {
      return rawDefault;
    }
    const textSpan = button?.querySelector('.add-to-cart-text');
    const curText = textSpan ? textSpan.textContent.trim() : button?.textContent.trim();
    if (curText && !['Max Quantity in Cart', 'Sold Out', 'Adding...'].includes(curText)) {
      if (button) button.dataset.defaultText = curText;
      return curText;
    }
    return 'Add to Cart';
  }

  /**
   * Forcefully clears all native form error messages and cancels pending error timeouts
   */
  #clearNativeErrors() {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    this.#clearLiveRegionText();

    const { addToCartTextError } = this.refs;
    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.style.display = 'none';
      addToCartTextError.removeAttribute('aria-live');
      Array.from(addToCartTextError.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = '';
        }
      });
    }

    const allErrors = this.querySelectorAll(
      '.product-form-text__error, [ref="addToCartTextError"], .form__message, .product-form__error-message-wrapper, .quantity-rule-error'
    );
    allErrors.forEach((el) => {
      el.classList.add('hidden');
      el.style.display = 'none';
      el.removeAttribute('aria-live');
      Array.from(el.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = '';
        }
      });
    });
  }

  /**
   * Immediate strict UI & state reset executed on variant switch.
   * Force-clears native errors, resets quantity input to 1 & syncs cart quantity,
   * forcefully enables submit buttons, removes disabled classes, and resets stock notice.
   * @param {string | number | null} [variantId]
   */
  #resetFormAndButtonState(variantId) {
    // 1. Force-clear native errors
    this.#clearNativeErrors();

    // 2. Reset quantity input value to 1 and sync data-cart-quantity for the selected variant
    const cartQty = variantId ? this.#getCartQtyForVariant(variantId) : 0;
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantityInput) {
      quantityInput.value = '1';
      quantityInput.removeAttribute('disabled');
      quantityInput.disabled = false;
      quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    }

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }
    if (quantitySelector?.updateButtonStates) {
      quantitySelector.updateButtonStates();
    }

    // 3. Forcefully unlock submit buttons and remove standard theme disabled classes
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    for (const container of allAddToCartContainers) {
      container.enable();
      const button =
        container.refs.addToCartButton ||
        container.querySelector('button[name="add"], button[type="submit"]');
      if (button) {
        button.disabled = false;
        button.removeAttribute('disabled');
        button.classList.remove(
          'disabled',
          'is-disabled',
          'button--disabled',
          'thk-btn-muted',
          'product-form__submit--disabled',
          'product-card__atc-btn--error'
        );
        const defaultText = this.#getDefaultButtonText(button);
        const textSpan = button.querySelector('.add-to-cart-text');
        if (textSpan) {
          textSpan.textContent = defaultText;
        } else {
          button.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
              node.textContent = ` ${defaultText} `;
            }
          });
        }
      }
    }

    // 4. Reset stock notice & plus button
    const stockNotice =
      this.querySelector('#stock-notice') ||
      document.querySelector(`#stock-notice-${this.dataset.sectionId}`);
    if (stockNotice) {
      stockNotice.classList.add('hidden');
      stockNotice.innerHTML = '';
    }

    const plusButton = /** @type {HTMLButtonElement | null} */ (
      quantitySelector?.querySelector('button[name="plus"]')
    );
    if (plusButton) plusButton.disabled = false;
  }

  /**
   * Immediate strict UI reset executed before asynchronous variant operations.
   */
  #resetScarcityUi() {
    this.#resetFormAndButtonState(null);
  }

  /**
   * Synchronously filters window.thkCartCache for items matching variantId
   * @param {string | number} variantId
   * @returns {number}
   */
  #getCartQtyForVariant(variantId) {
    if (!variantId) return 0;
    const items = window.thkCartCache;
    let qty = 0;
    if (Array.isArray(items)) {
      const targetId = variantId.toString();
      for (const item of items) {
        if (
          item.variant_id?.toString() === targetId ||
          item.id?.toString() === targetId
        ) {
          qty += Number(item.quantity) || 0;
        }
      }
    }
    return qty;
  }

  /**
   * Synchronously resolves current variant ID from event detail or variant picker inputs
   * @param {ProductSelectEvent} [event]
   * @returns {string | null}
   */
  #resolveCurrentVariantId(event) {
    if (event?.detail?.variantId) {
      return event.detail.variantId.toString();
    }

    const section = this.closest('.shopify-section') || document;
    const picker = section.querySelector('variant-picker');
    if (!picker) return null;

    const checkedInputs = Array.from(picker.querySelectorAll('input:checked, select'));
    const selectedValues = checkedInputs
      .map((input) => {
        if (input instanceof HTMLSelectElement) return input.value.trim();
        return (/** @type {HTMLInputElement} */ (input).value || '').trim();
      })
      .filter(Boolean);

    if (selectedValues.length === 0) return null;

    const variants = this.#getVariantsInventory();
    if (!variants) return null;

    for (const [vId, vData] of Object.entries(variants)) {
      if (vData.options && Array.isArray(vData.options)) {
        if (
          vData.options.length === selectedValues.length &&
          vData.options.every((opt, idx) => opt.trim() === selectedValues[idx])
        ) {
          return vId;
        }
        if (
          vData.options.length === selectedValues.length &&
          vData.options.every((opt) => selectedValues.includes(opt.trim()))
        ) {
          return vId;
        }
      } else if (vData.title && selectedValues.length === 1 && vData.title.trim() === selectedValues[0]) {
        return vId;
      }
    }
    return null;
  }

  /**
   * Updates quantity selector with cart data for current variant and evaluates scarcity
   * @param {Cart | { items: any[] } | null} [cart] - Optional cart object with items array
   */
  #updateCartQuantity(cart) {
    if (cart && Array.isArray(cart.items)) {
      window.thkCartCache = cart.items;
    } else if (Array.isArray(cart)) {
      window.thkCartCache = cart;
    }

    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartQty = this.#getCartQtyForVariant(variantIdInput);

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    // Evaluate real-time luxury scarcity
    this.#evaluateScarcity(variantIdInput, cartQty);
  }

  /**
   * Helper to fetch variant inventory mapping
   * @returns {Record<string, {id: number, inventory_quantity: number, inventory_management: string|null, inventory_policy: string, available: boolean}> | null}
   */
  #getVariantsInventory() {
    const inventoryScript =
      this.querySelector('script[data-variants-inventory]') ||
      document.querySelector(`script#thk-variants-inventory-${this.dataset.sectionId}`);
    if (!inventoryScript) return null;
    try {
      return JSON.parse(inventoryScript.textContent || '{}');
    } catch (e) {
      console.warn('[scarcity] Error parsing variant inventory JSON:', e);
      return null;
    }
  }

  /**
   * Evaluates luxury scarcity UX based on variant inventory and current cart quantity
   * @param {string} variantId
   * @param {number} cartQty
   */
  #evaluateScarcity(variantId, cartQty) {
    const variantsInventory = this.#getVariantsInventory();
    const variantInfo = variantsInventory ? variantsInventory[variantId] : null;

    const stockNotice =
      this.querySelector('#stock-notice') ||
      document.querySelector(`#stock-notice-${this.dataset.sectionId}`);
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const quantitySelector = this.querySelector('quantity-selector-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      quantitySelector?.querySelector('input[ref="quantityInput"]')
    );
    const plusButton = /** @type {HTMLButtonElement | null} */ (
      quantitySelector?.querySelector('button[name="plus"]')
    );

    // Check if variant inventory is tracked and constrained
    const allowsOversell = variantInfo && variantInfo.inventory_policy === 'continue';
    const inventoryQty =
      variantInfo && variantInfo.inventory_quantity !== undefined && variantInfo.inventory_quantity !== null
        ? Number(variantInfo.inventory_quantity)
        : Infinity;
    const isManaged = !allowsOversell && Number.isFinite(inventoryQty);
    const isAvailable = variantInfo ? variantInfo.available !== false : true;

    // Dynamically clamp quantity input max
    if (isManaged && quantityInput && Number.isFinite(inventoryQty) && inventoryQty > 0) {
      quantityInput.max = inventoryQty.toString();
      if (quantitySelector?.updateConstraints) {
        quantitySelector.updateConstraints(
          quantityInput.min || '1',
          inventoryQty.toString(),
          quantityInput.step || '1'
        );
      }
    }

    // Step 2 State Logic:
    // Case A: Completely Sold Out / Inventory <= 0 or isAvailable === false
    if (!isAvailable || (isManaged && inventoryQty <= 0)) {
      for (const container of allAddToCartContainers) {
        container.disable();
        const button = container.refs.addToCartButton || container.querySelector('button[name="add"]');
        if (button) {
          button.disabled = true;
          button.classList.remove('thk-btn-muted');
          const textSpan = button.querySelector('.add-to-cart-text');
          if (textSpan) textSpan.textContent = 'Sold Out';
        }
      }
      if (stockNotice) {
        stockNotice.classList.add('hidden');
        stockNotice.innerHTML = '';
      }
      if (plusButton) plusButton.disabled = true;
      return;
    }

    // Case B: Limit Reached / Cart Qty >= Inventory
    if ((isManaged && inventoryQty > 0 && cartQty >= inventoryQty) || (inventoryQty === 1 && cartQty >= 1)) {
      for (const container of allAddToCartContainers) {
        container.disable();
        const button = container.refs.addToCartButton || container.querySelector('button[name="add"]');
        if (button) {
          button.disabled = true;
          button.classList.add('thk-btn-muted');
          const textSpan = button.querySelector('.add-to-cart-text');
          if (textSpan) textSpan.textContent = 'Max Quantity in Cart';
        }
      }

      if (stockNotice) {
        stockNotice.innerHTML = `You have added the only available piece in this size. <a href="/cart" style="text-decoration: underline;">Go to Checkout</a>`;
        stockNotice.classList.remove('hidden');

        const cartLink = stockNotice.querySelector('a');
        cartLink?.addEventListener('click', (e) => {
          const cartDrawer =
            document.querySelector('#cart-drawer') || document.querySelector('theme-drawer#cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            e.preventDefault();
            cartDrawer.open();
          } else if (cartDrawer && typeof cartDrawer.show === 'function') {
            e.preventDefault();
            cartDrawer.show();
          }
        });
      }

      if (plusButton) plusButton.disabled = true;
      return;
    }

    // Case C: Low Stock Browsing / Inventory = 1 & Cart Qty = 0
    if (isManaged && inventoryQty === 1 && cartQty === 0) {
      for (const container of allAddToCartContainers) {
        container.enable();
        const button = container.refs.addToCartButton || container.querySelector('button[name="add"]');
        if (button) {
          button.disabled = false;
          button.removeAttribute('disabled');
          button.classList.remove(
            'disabled',
            'is-disabled',
            'button--disabled',
            'thk-btn-muted',
            'product-form__submit--disabled',
            'product-card__atc-btn--error'
          );
          const textSpan = button.querySelector('.add-to-cart-text');
          const defaultText = this.#getDefaultButtonText(button);
          if (textSpan) textSpan.textContent = defaultText;
        }
      }

      if (stockNotice) {
        stockNotice.textContent = 'Only 1 piece crafted in this size.';
        stockNotice.classList.remove('hidden');
      }

      if (plusButton && quantityInput && Number(quantityInput.value) >= 1) {
        plusButton.disabled = true;
      }
      return;
    }

    // Case D: Normal Browsing / In Stock (Inventory > 1 & Cart Qty < Inventory, or oversell allowed)
    for (const container of allAddToCartContainers) {
      container.enable();
      const button = container.refs.addToCartButton || container.querySelector('button[name="add"]');
      if (button) {
        button.disabled = false;
        button.removeAttribute('disabled');
        button.classList.remove(
          'disabled',
          'is-disabled',
          'button--disabled',
          'thk-btn-muted',
          'product-form__submit--disabled',
          'product-card__atc-btn--error'
        );
        const textSpan = button.querySelector('.add-to-cart-text');
        const defaultText = this.#getDefaultButtonText(button);
        if (textSpan) textSpan.textContent = defaultText;
      }
    }

    if (stockNotice) {
      stockNotice.classList.add('hidden');
      stockNotice.innerHTML = '';
    }

    if (plusButton) {
      const curVal = Number(quantityInput?.value || 1);
      plusButton.disabled = isManaged && curVal + cartQty >= inventoryQty;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          window.thkCartCache = detail.items;
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => {
            if (cart?.items) window.thkCartCache = cart.items;
            this.#updateCartQuantity(cart);
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /**
   * Updates data-cart-quantity when cart:updated event is fired
   * @param {CustomEvent} event
   */
  #onCustomCartUpdate = (event) => {
    if (!this.#getVariantIdInput()) return;
    const cart = event?.detail?.cart;
    if (cart && Array.isArray(cart.items)) {
      window.thkCartCache = cart.items;
      this.#updateCartQuantity(/** @type {Cart} */ (cart));
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.style.display = '';
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          const isLimitError =
            response.status === 422 ||
            (response.description && /cart|limit|max|available/i.test(response.description)) ||
            (response.message && /cart|limit|max|available/i.test(response.message));

          if (isLimitError) {
            // Apply real-time luxury scarcity state
            const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
              this.querySelectorAll('add-to-cart-component')
            );
            for (const container of allAddToCartContainers) {
              container.disable();
              const button = container.refs.addToCartButton || container.querySelector('button[name="add"]');
              if (button) {
                button.disabled = true;
                const textSpan = button.querySelector('.add-to-cart-text');
                if (textSpan) textSpan.textContent = 'Max Quantity in Cart';
              }
            }

            const stockNotice =
              this.querySelector('#stock-notice') ||
              document.querySelector(`#stock-notice-${this.dataset.sectionId}`);
            if (stockNotice) {
              stockNotice.innerHTML = `You have added the only available piece in this size. <a href="/cart" class="stock-notice__cart-link" style="text-decoration: underline;">Go to Checkout</a>`;
              stockNotice.classList.remove('hidden');
              const cartLink = stockNotice.querySelector('a');
              cartLink?.addEventListener('click', (e) => {
                const cartDrawer = document.querySelector('#cart-drawer');
                if (cartDrawer && typeof cartDrawer.show === 'function') {
                  e.preventDefault();
                  cartDrawer.show();
                }
              });
            }

            const quantitySelector = this.querySelector('quantity-selector-component');
            const plusButton = /** @type {HTMLButtonElement | null} */ (
              quantitySelector?.querySelector('button[name="plus"]')
            );
            if (plusButton) plusButton.disabled = true;

            // Open cart drawer so user sees their item
            const drawer = document.querySelector('theme-drawer#cart-drawer') || document.getElementById('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              drawer.open();
            } else if (drawer && typeof drawer.show === 'function') {
              drawer.show();
            }

            this.#refreshCart()
              .then((ajaxCart) => this.#updateCartQuantity(ajaxCart))
              .catch(() => {});

            return;
          }

          if (!addToCartTextError) return;
          addToCartTextError.style.display = '';
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#refreshCart()
            .then((ajaxCart) => {
              if (ajaxCart?.items) window.thkCartCache = ajaxCart.items;
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }

              return ajaxCart;
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          const isLimitError =
            response.status === 422 ||
            (response.description && /cart|limit|max|available/i.test(response.description)) ||
            (response.message && /cart|limit|max|available/i.test(response.message));

          if (isLimitError) {
            const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
              this.querySelectorAll('add-to-cart-component')
            );
            for (const container of allAddToCartContainers) {
              container.disable();
              const button = container.refs.addToCartButton || container.querySelector('button[name="add"]');
              if (button) {
                button.disabled = true;
                const textSpan = button.querySelector('.add-to-cart-text');
                if (textSpan) textSpan.textContent = 'Max Quantity in Cart';
              }
            }

            const stockNotice =
              this.querySelector('#stock-notice') ||
              document.querySelector(`#stock-notice-${this.dataset.sectionId}`);
            if (stockNotice) {
              stockNotice.innerHTML = `You have added the only available piece in this size. <a href="/cart" class="stock-notice__cart-link" style="text-decoration: underline;">Go to Checkout</a>`;
              stockNotice.classList.remove('hidden');
              const cartLink = stockNotice.querySelector('a');
              cartLink?.addEventListener('click', (e) => {
                const cartDrawer = document.querySelector('#cart-drawer');
                if (cartDrawer && typeof cartDrawer.show === 'function') {
                  e.preventDefault();
                  cartDrawer.show();
                }
              });
            }

            const quantitySelector = this.querySelector('quantity-selector-component');
            const plusButton = /** @type {HTMLButtonElement | null} */ (
              quantitySelector?.querySelector('button[name="plus"]')
            );
            if (plusButton) plusButton.disabled = true;

            const drawer = document.querySelector('theme-drawer#cart-drawer') || document.getElementById('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              drawer.open();
            } else if (drawer && typeof drawer.show === 'function') {
              drawer.show();
            }

            this.#refreshCart()
              .then((ajaxCart) => this.#updateCartQuantity(ajaxCart))
              .catch(() => {});

            return;
          }

          if (!addToCartTextError) return;
          addToCartTextError.style.display = '';
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        if (cart?.items) window.thkCartCache = cart.items;
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    // Hold the in-flight section-fetch promise so the queue drain can await it before reading the
    // resolved variant id.
    this.#pendingVariantChange = event.promise;

    // Step 1: Resolve the newly selected variant ID immediately
    const immediateVariantId = this.#resolveCurrentVariantId(event);

    // Step 2 & 3: Force-clear native errors, reset quantity to 1 & sync cart quantity, forcefully unlock button
    this.#resetFormAndButtonState(immediateVariantId);

    // Step 4: Synchronously evaluate luxury scarcity from local cache with ZERO network latency
    if (immediateVariantId) {
      const immediateQty = this.#getCartQtyForVariant(immediateVariantId);
      this.#evaluateScarcity(immediateVariantId, immediateQty);
    }

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;
      if (generation !== this.#variantChangeGeneration) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      // Update inventory script if present in new HTML
      const newInventoryScript = html.querySelector('script[data-variants-inventory]');
      const currentInventoryScript = this.querySelector('script[data-variants-inventory]');
      if (newInventoryScript && currentInventoryScript) {
        currentInventoryScript.textContent = newInventoryScript.textContent;
      }

      // Post-morph clean up: ensure native errors are hidden and correct variant state applied
      this.#clearNativeErrors();

      if (resource?.id && generation === this.#variantChangeGeneration) {
        const cartQty = this.#getCartQtyForVariant(resource.id);
        const quantityInput = /** @type {HTMLInputElement | null} */ (
          this.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );
        if (quantityInput) {
          quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
        }
        if (quantitySelector?.setCartQuantity) {
          quantitySelector.setCartQuantity(cartQty);
        }

        // Force-unlock if variant is available
        if (resource.available !== false) {
          currentAddToCartButtonContainer?.enable();
          if (currentAddToCartButton) {
            currentAddToCartButton.disabled = false;
            currentAddToCartButton.removeAttribute('disabled');
            currentAddToCartButton.classList.remove(
              'disabled',
              'is-disabled',
              'button--disabled',
              'thk-btn-muted',
              'product-card__atc-btn--error'
            );
          }
        }

        this.#evaluateScarcity(resource.id.toString(), cartQty);
      }
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        await this.#drainAddToCartQueue();
      }
    }
  };

  /**
   * Drains the add-to-cart queue accumulated while a variant change was in flight.
   *
   * Each queued add resolves against the selection and generation that were active when Add was
   * clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
   * variant id is never sent. The add-to-cart button is already disabled for unavailable
   * selections in #onProductSelect, so no further UI change is needed.
   */
  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];

    /** @type {Array<{variantId: string, quantity: number}>} */
    const resolvedItems = [];
    for (const item of queuedItems) {
      const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
      if (resolvedItem) {
        resolvedItems.push(resolvedItem);
      }
    }

    this.#processBatchAddToCart(resolvedItems);
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number} | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) return null;

    return {
      variantId,
      quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints),
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;
    /** @type {boolean | undefined} */
    let available;
    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await item.pendingVariantChange
        );
        const resource = result?.detail?.resource;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch {
        const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
        }));
        resolvedVariantId = resolvedVariant.variantId;
        available = resolvedVariant.available;
        quantityConstraints = resolvedVariant.quantityConstraints;
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return { variantId, quantityConstraints };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : Math.max(max - cartQuantity, min);

    let normalizedQuantity = quantity;
    if ((quantity - min) % step !== 0) {
      normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
    }

    return Math.max(min, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * Resolves a queued selection using the server-side section renderer when the original in-flight
   * request was aborted by a later variant selection.
   * @param {string | null} variantResolutionUrl
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };

    const response = await fetch(variantResolutionUrl, { credentials: 'same-origin' });
    if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) return { variantId: null, available: false, quantityConstraints };

    const resource = JSON.parse(textContent);
    if (!resource || typeof resource !== 'object') return { variantId: null, available: false, quantityConstraints };

    return {
      variantId: resource.id != null ? String(resource.id) : null,
      available: resource.available !== false,
      quantityConstraints,
    };
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  /**
   * Whether the current selection's add-to-cart button is disabled (unavailable selection).
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
